import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

export type PauseUnit = 'days' | 'weeks' | 'months' | 'years';

@Component({
    selector: 'app-pause-subscription-dialog.component',
    templateUrl: './pause-subscription-dialog.component.html',
    styleUrls: ['./pause-subscription-dialog.component.css'],
    standalone: false
})
export class PauseSubscriptionDialogComponent implements OnInit {

  pauseForm!: UntypedFormGroup;
  readonly maxDuration = 365;
  readonly allUnits: { label: string; value: PauseUnit }[] = [
    { label: 'Days', value: 'days' },
    { label: 'Weeks', value: 'weeks' },
    { label: 'Months', value: 'months' },
    { label: 'Years', value: 'years' },
  ];
  allowedUnits: PauseUnit[] = ['days'];

  // Template uses this
  get units(): { label: string; value: PauseUnit }[] {
    return this.allUnits.filter(u => this.allowedUnits.includes(u.value));
  }

  constructor(
    private dialogRef: MatDialogRef<PauseSubscriptionDialogComponent>,
    private fb: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: {
      current_status?: string;
      defaultDuration?: number;
      defaultUnit?: PauseUnit;
      pause_units_allowed?: PauseUnit[];
      nextChargeDateLabel?: string;
    }
  ) {}

  ngOnInit(): void {
    this.allowedUnits = (this.data?.pause_units_allowed?.length
      ? this.data.pause_units_allowed
      : ['days']) as PauseUnit[];
    const defaultUnit = (this.data?.defaultUnit ?? this.allowedUnits[0]) as PauseUnit;
    const initialUnit = this.allowedUnits.includes(defaultUnit) ? defaultUnit : this.allowedUnits[0];

    // 3) Build form
    this.pauseForm = this.fb.group({
      duration: [
        this.data?.defaultDuration ?? 1,
        [Validators.required, Validators.min(1)], // max added dynamically
      ],
      unit: [
        initialUnit,
        [Validators.required, this.unitAllowedValidator],
      ],
    });
    this.applyDurationMaxValidator();
    this.pauseForm.get('unit')?.valueChanges.subscribe(() => {
      const unitCtrl = this.pauseForm.get('unit');
      if (unitCtrl && !this.allowedUnits.includes(unitCtrl.value as PauseUnit)) {
        unitCtrl.setValue(this.allowedUnits[0], { emitEvent: false });
      }
      this.applyDurationMaxValidator();
    });
  }

  private unitAllowedValidator = (control: AbstractControl) => {
    const value = control.value as PauseUnit;
    return this.allowedUnits.includes(value) ? null : { unitNotAllowed: true };
  };

  private applyDurationMaxValidator(): void {
    const durationCtrl = this.pauseForm.get('duration');
    if (!durationCtrl) { return; }
    const unit = (this.pauseForm.get('unit')?.value || this.allowedUnits[0] || 'days') as PauseUnit;
    let max: number;
    switch (unit) {
      case 'weeks':
        max = Math.floor(this.maxDuration / 7);   // 365 -> 52
        break;
      case 'months':
        max = Math.floor(this.maxDuration / 30);  // 365 -> 12 (policy)
        break;
      case 'years':
        max = Math.floor(this.maxDuration / 365); // 365 -> 1
        break;
      case 'days':
      default:
        max = this.maxDuration;                   // 365
        break;
    }

    durationCtrl.setValidators([
      Validators.required,
      Validators.min(1),
      Validators.max(max),
    ]);
    durationCtrl.updateValueAndValidity({ emitEvent: false });
  }

  get currentMax(): number {
    const unit = (this.pauseForm?.get('unit')?.value || this.allowedUnits[0] || 'days') as PauseUnit;
    switch (unit) {
      case 'weeks':
        return Math.floor(this.maxDuration / 7);
      case 'months':
        return Math.floor(this.maxDuration / 30);
      case 'years':
        return Math.floor(this.maxDuration / 365);
      case 'days':
      default:
        return this.maxDuration;
    }
  }

  closeAction(): void {
    this.dialogRef.close(null);
  }

  saveAction(): void {
    this.pauseForm.markAllAsTouched();

    if (!this.pauseForm.valid) {
      this.snackBar.open('Please provide a valid pause duration.', 'OK', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['snackbar-error', 'centered-snackbar'],
      });
      return;
    }

    const payload = {
      duration: Number(this.pauseForm.value.duration),
      unit: this.pauseForm.value.unit as PauseUnit,
    };

    this.dialogRef.close(payload);
  }

  get durationCtrl(): AbstractControl | null {
    return this.pauseForm?.get('duration') ?? null;
  }

  get unitCtrl(): AbstractControl | null {
    return this.pauseForm?.get('unit') ?? null;
  }

  getDurationError(): string {
    const c = this.durationCtrl;
    if (!c || !c.touched) { return ''; }
    if (c.hasError('required')) { return 'Duration is required.'; }
    if (c.hasError('min')) { return 'Duration must be at least 1.'; }
    if (c.hasError('max')) {
      const unit = (this.pauseForm?.get('unit')?.value || 'days') as PauseUnit;
      return `Duration cannot exceed ${this.currentMax} ${unit}.`;
    }
    return 'Invalid duration.';
  }

  getUnitError(): string {
    const c = this.unitCtrl;
    if (!c || !c.touched) { return ''; }
    if (c.hasError('required')) { return 'Unit is required.'; }
    if (c.hasError('unitNotAllowed')) { return 'This unit is not allowed for this plan.'; }
    return 'Invalid unit.';
  }
}
